import {DragEndEvent} from '@dnd-kit/core';
import {Button, FormControl, FormField, IconButton} from 'platform/components';
import {Box, Heading, HStack, Icon, VStack} from 'platform/foundation';

import {useFieldArray, UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {
  buildSortableItemId,
  DnDContext,
  parseDndItemId,
  Sortable,
  SortableWrapper,
} from '@dms/features/dnd';
import i18n from '@dms/i18n';

import {RecordAuditDefinitionFormValue} from '../types/RecordAuditDefinitionFormValue';

const CONTAINER_ID = 'record-audit-definition-sections';

interface SectionsControlProps {
  formApi: UseFormReturn<RecordAuditDefinitionFormValue>;
  control: FormControl<RecordAuditDefinitionFormValue>;
}

export function SectionsControl(props: SectionsControlProps) {
  const sectionsControl = useFieldArray({
    control: props.formApi.control,
    name: 'sections',
  });

  const handleAddSection = () => {
    sectionsControl.append({
      id: crypto.randomUUID(),
      name: '',
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    if (isNil(event.over) || event.active.id === event.over.id) {
      return;
    }

    const fromIndex = parseDndItemId(event.active.id).position;
    const toIndex = parseDndItemId(event.over.id).position;

    if (isNotNil(fromIndex) && isNotNil(toIndex)) {
      sectionsControl.move(fromIndex, toIndex);
    }
  };

  return (
    <VStack spacing={4}>
      <Heading size={4}>{i18n.t('entity.audits.checklist.sections')}</Heading>
      <DnDContext onDragEnd={onDragEnd}>
        <SortableWrapper
          containerId={CONTAINER_ID}
          items={sectionsControl.fields.map((field) => ({id: field.id, url: field.id}))}
        >
          {sectionsControl.fields.map((field, index) => (
            <Sortable
              key={field.id}
              id={buildSortableItemId(field.id, CONTAINER_ID, field.id, index)}
              withHandle
            >
              {({handleProps}) => (
                <HStack spacing={2} align="flex-end">
                  <Box paddingBottom={2}>
                    <div
                      {...handleProps}
                      style={{
                        cursor: 'grab',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      aria-label={i18n.t('general.actions.reorder')}
                    >
                      <Icon value="action/drag_indicator" size={4} />
                    </div>
                  </Box>
                  <Box flex={1}>
                    <FormField
                      type="text"
                      control={props.control}
                      name={`sections.${index}.name`}
                      label={i18n.t('entity.audits.checklist.sectionName')}
                      isRequired
                    />
                  </Box>
                  <IconButton
                    severity="danger"
                    icon="action/delete"
                    aria-label={i18n.t('general.actions.delete')}
                    onClick={() => sectionsControl.remove(index)}
                  />
                </HStack>
              )}
            </Sortable>
          ))}
        </SortableWrapper>
      </DnDContext>
      <Box>
        <Button
          variant="ghostLink"
          leftIcon="content/add_circle"
          title={i18n.t('entity.audits.checklist.addSection')}
          onClick={handleAddSection}
        />
      </Box>
    </VStack>
  );
}
